import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";
import MonApproche from "./pages/MonApproche";
import Contact from "./pages/Contact";
import Dashboard from "./pages/Dashboard";
import NotFound from "./pages/NotFound";
import NaturopathyApp from "./pages/NaturopathyApp";
import PatientsList from "./pages/PatientsList";
import PatientFile from "./pages/PatientFile";
import Auth from "./pages/Auth";
import TreatmentsLibrary from "./pages/TreatmentsLibrary";
import VitalityAssessment from "./pages/VitalityAssessment";
import VitalityScoring from "./pages/VitalityScoring";
import MedicinalPlantsLibrary from "./pages/MedicinalPlantsLibrary";
import BachFlowersLibrary from "./pages/BachFlowersLibrary";
import ImportPlants from "./pages/ImportPlants";
import ImportBachFlowers from "./pages/ImportBachFlowers";
import AccountSettings from "./pages/AccountSettings";
import Appointments from "./pages/Appointments";
import AvailabilitySettings from "./pages/AvailabilitySettings";
import Billing from "./pages/Billing";
import EventsManagement from "./pages/EventsManagement";
import ProductsManagement from "./pages/ProductsManagement";
import AccompanimentsManagement from "./pages/AccompanimentsManagement";
import SessionReport from "./pages/SessionReport";
import SeasonalWellnessManagement from "./pages/SeasonalWellnessManagement";
import GiftCardManagement from "./pages/GiftCardManagement";
import GiftCardSuccess from "./pages/GiftCardSuccess";
import ProductSuccess from "./pages/ProductSuccess";
import ClayLibrary from "./pages/ClayLibrary";
import AromatherapyLibrary from "./pages/AromatherapyLibrary";
import GemmotherapyLibrary from "./pages/GemmotherapyLibrary";
import VegetableOilsLibrary from "./pages/VegetableOilsLibrary";
import HydrolatsLibrary from "./pages/HydrolatsLibrary";
import ApitherapyLibrary from "./pages/ApitherapyLibrary";
import IridologyDashboard from "./pages/IridologyDashboard";
import IridologySession from "./pages/IridologySession";
import Certification from "./pages/Certification";
import NewsletterConfirm from "./pages/NewsletterConfirm";
import NewsletterManagement from "./pages/NewsletterManagement";
import SocialPublishing from "./pages/SocialPublishing";
import LocationsManagement from "./pages/LocationsManagement";
import GDriveCallback from "./pages/GDriveCallback";
import NaturopatheMontesquieu from "./pages/seo/NaturopatheMontesquieu";
import NaturopatheVillefranche from "./pages/seo/NaturopatheVillefranche";
import NaturopatheStress from "./pages/seo/NaturopatheStress";
import NaturopatheDigestion from "./pages/seo/NaturopatheDigestion";
import NaturopatheSommeil from "./pages/seo/NaturopatheSommeil";
import NaturopatheFatigue from "./pages/seo/NaturopatheFatigue";
import FleursDeBach from "./pages/seo/FleursDeBach";
import TdahDysEnfants from "./pages/seo/TdahDysEnfants";
import ParentaliteBurnout from "./pages/seo/ParentaliteBurnout";
import ConsultationsVisio from "./pages/seo/ConsultationsVisio";
import NaturopatheToulouse from "./pages/seo/NaturopatheToulouse";
import { ProtectedRoute } from "./components/ProtectedRoute";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Routes>
          <Route path="/auth" element={<Auth />} />
          <Route path="/" element={<Index />} />
          <Route path="/mon-approche" element={<MonApproche />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/certification" element={<Certification />} />
          <Route path="/newsletter/confirm" element={<NewsletterConfirm />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/app" element={<ProtectedRoute><NaturopathyApp /></ProtectedRoute>} />
          <Route path="/library" element={<ProtectedRoute><TreatmentsLibrary /></ProtectedRoute>} />
          <Route path="/treatments-library" element={<ProtectedRoute><TreatmentsLibrary /></ProtectedRoute>} />
          <Route path="/clay-library" element={<ProtectedRoute><ClayLibrary /></ProtectedRoute>} />
          <Route path="/aromatherapy-library" element={<ProtectedRoute><AromatherapyLibrary /></ProtectedRoute>} />
          <Route path="/gemmotherapy-library" element={<ProtectedRoute><GemmotherapyLibrary /></ProtectedRoute>} />
          <Route path="/vegetable-oils-library" element={<ProtectedRoute><VegetableOilsLibrary /></ProtectedRoute>} />
          <Route path="/hydrolats-library" element={<ProtectedRoute><HydrolatsLibrary /></ProtectedRoute>} />
          <Route path="/apitherapy-library" element={<ProtectedRoute><ApitherapyLibrary /></ProtectedRoute>} />
          <Route path="/plants" element={<ProtectedRoute><MedicinalPlantsLibrary /></ProtectedRoute>} />
          <Route path="/bach-flowers" element={<ProtectedRoute><BachFlowersLibrary /></ProtectedRoute>} />
          <Route path="/import-plants" element={<ProtectedRoute><ImportPlants /></ProtectedRoute>} />
          <Route path="/import-bach-flowers" element={<ProtectedRoute><ImportBachFlowers /></ProtectedRoute>} />
          <Route path="/patients" element={<ProtectedRoute><PatientsList /></ProtectedRoute>} />
          <Route path="/patients/:id" element={<ProtectedRoute><PatientFile /></ProtectedRoute>} />
          <Route path="/appointments" element={<ProtectedRoute><Appointments /></ProtectedRoute>} />
          <Route path="/availability-settings" element={<ProtectedRoute><AvailabilitySettings /></ProtectedRoute>} />
          <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
          <Route path="/events-management" element={<ProtectedRoute><EventsManagement /></ProtectedRoute>} />
          <Route path="/products-management" element={<ProtectedRoute><ProductsManagement /></ProtectedRoute>} />
          <Route path="/accompaniments-management" element={<ProtectedRoute><AccompanimentsManagement /></ProtectedRoute>} />
          <Route path="/vitality" element={<ProtectedRoute><VitalityAssessment /></ProtectedRoute>} />
          <Route path="/vitality/:id" element={<ProtectedRoute><VitalityAssessment /></ProtectedRoute>} />
          <Route path="/vitality-scoring" element={<ProtectedRoute><VitalityScoring /></ProtectedRoute>} />
          <Route path="/session-report" element={<ProtectedRoute><SessionReport /></ProtectedRoute>} />
          <Route path="/seasonal-wellness" element={<ProtectedRoute><SeasonalWellnessManagement /></ProtectedRoute>} />
          <Route path="/gift-cards" element={<ProtectedRoute><GiftCardManagement /></ProtectedRoute>} />
          <Route path="/gift-card-success" element={<GiftCardSuccess />} />
          <Route path="/product-success" element={<ProductSuccess />} />
          <Route path="/account-settings" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
          <Route path="/iridology" element={<ProtectedRoute><IridologyDashboard /></ProtectedRoute>} />
          <Route path="/iridology/:id" element={<ProtectedRoute><IridologySession /></ProtectedRoute>} />
          <Route path="/newsletter-management" element={<ProtectedRoute><NewsletterManagement /></ProtectedRoute>} />
          <Route path="/social-publishing" element={<ProtectedRoute><SocialPublishing /></ProtectedRoute>} />
          <Route path="/locations" element={<ProtectedRoute><LocationsManagement /></ProtectedRoute>} />
          <Route path="/gdrive-callback" element={<GDriveCallback />} />
          {/* SEO public pages */}
          <Route path="/naturopathe-montesquieu-lauragais" element={<NaturopatheMontesquieu />} />
          <Route path="/naturopathe-villefranche-de-lauragais" element={<NaturopatheVillefranche />} />
          <Route path="/naturopathe-stress" element={<NaturopatheStress />} />
          <Route path="/naturopathe-digestion" element={<NaturopatheDigestion />} />
          <Route path="/naturopathe-sommeil" element={<NaturopatheSommeil />} />
          <Route path="/naturopathe-fatigue" element={<NaturopatheFatigue />} />
          <Route path="/fleurs-de-bach" element={<FleursDeBach />} />
          <Route path="/tdah-dys-enfants-atypiques" element={<TdahDysEnfants />} />
          <Route path="/parentalite-burn-out-parental" element={<ParentaliteBurnout />} />
          <Route path="/consultations-visio-naturopathe-france" element={<ConsultationsVisio />} />
          <Route path="/naturopathe-toulouse" element={<NaturopatheToulouse />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;